import type { MePortal } from "@baton/api";
import type { MeView } from "../app/queries.ts";
import { accessOf, holdsArea } from "../shell/state/access.ts";

/**
 * Who reaches the Tickets screens of a Team, as the client reads `GET /me` (028 FR-028-001,
 * FR-028-046, FR-028-058, `research.md` D-2804): a portal worker is a member of the Team holding the
 * `clients` area at `portal`, which an owner holds without a grant; a client member is a person
 * holding no membership of the Team and a live membership of one of its portals, which `/me` lists
 * under `portals` with each portal's Team, client and status. The server decides every route; this
 * module draws the same conclusion in one place, so the shell picks the right frame before any read.
 */
export function worksPortals(me: MeView | undefined, ws: string): boolean {
  return holdsArea(accessOf(me, ws), "clients", "portal");
}

/** The caller's portals in the Team `ws`, open and closed, in the order `/me` lists them. */
export function portalsInTeam(me: MeView | undefined, ws: string): readonly MePortal[] {
  return (me?.portals ?? []).filter((portal) => portal.workspace.slug === ws);
}

/** Whether `/me` makes the caller a member of the Team `ws`. */
export function isTeamMember(me: MeView | undefined, ws: string): boolean {
  return me?.memberships.some((membership) => membership.slug === ws) ?? false;
}

export interface TeamPortals {
  readonly workspace: MePortal["workspace"];
  readonly portals: readonly MePortal[];
}

/** The caller's open portals grouped by Team, the Teams in the order `/me` first names them: what a client member's switcher lists (FR-028-046). */
export function openPortalsByTeam(me: MeView | undefined): readonly TeamPortals[] {
  const groups = new Map<string, { workspace: MePortal["workspace"]; portals: MePortal[] }>();
  for (const portal of me?.portals ?? []) {
    if (portal.status !== "open") continue;
    const group = groups.get(portal.workspace.id) ?? { workspace: portal.workspace, portals: [] };
    group.portals.push(portal);
    groups.set(portal.workspace.id, group);
  }
  return [...groups.values()];
}
